import { useState } from 'react'
import { Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useProjectMutations, useProjects } from '../api'

/** Projects that can take time. Until now they had to be inserted by hand. */
export function ProjectsPage() {
  useBreadcrumb()
  const projects = useProjects()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { createProject } = useProjectMutations()

  const [draft, setDraft] = useState<{
    code: string; name: string; startDate: string; endDate: string; status: string
    billable: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Projects"
        subtitle="What time can be booked against."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', name: '', startDate: '', endDate: '', status: 'ACTIVE',
                billable: true,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New project
          </Button>
        }
      />

      <Card>
        {projects.isPending && <Skeleton className="m-4 h-20" />}
        {projects.isError && (
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        )}
        {projects.isSuccess && projects.data.length === 0 && (
          <EmptyState
            title="No project accepts time"
            description="A timesheet line has to point at a project, so nobody can book anything until one exists."
          />
        )}
        {projects.isSuccess && projects.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Project</th>
                <th className="p-3 text-start font-medium">Client</th>
                <th className="p-3 text-start font-medium">Billable</th>
                <th className="p-3 text-start font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.data.map((project) => (
                <tr key={project.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{project.code}</td>
                  <td className="p-3">{project.name}</td>
                  <td className="p-3">{project.clientName ?? '—'}</td>
                  <td className="p-3">{project.billable ? 'Yes' : 'No'}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={project.status}
                      tone={project.status === 'ACTIVE' ? 'success' : 'neutral'}
                      label={project.status.toLowerCase().replace('_', ' ')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title="New project"
        description="Only a project that accepts time appears in the timesheet picker."
        submitLabel="Save project"
        loading={createProject.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await createProject.mutateAsync({
                legalEntityId: entities.data?.[0]?.id ?? null,
                clientId: null,
                code: draft.code,
                name: draft.name,
                startDate: draft.startDate || null,
                endDate: draft.endDate || null,
                status: draft.status,
                billable: draft.billable,
                budgetHours: null,
                costCenterId: null,
                managerId: null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That project could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="project-code">Code</Label>
              <Input
                id="project-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="project-start">Starts</Label>
              <DatePicker
                id="project-start" value={draft.startDate}
                onChange={(event) => setDraft({ ...draft, startDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="project-end">Ends</Label>
              <DatePicker
                id="project-end" value={draft.endDate}
                onChange={(event) => setDraft({ ...draft, endDate: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                An end before the start is refused: no week could fall inside it.
              </p>
            </div>
            <div>
              <Label htmlFor="project-status">Status</Label>
              <Select
                id="project-status"
                className="w-full"
                value={draft.status}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                <option value="PLANNED">Planned — no time yet</option>
                <option value="ACTIVE">Active</option>
                <option value="ON_HOLD">On hold</option>
                <option value="COMPLETED">Completed</option>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  id="project-billable" type="checkbox" className="size-4"
                  checked={draft.billable}
                  onChange={(event) => setDraft({ ...draft, billable: event.target.checked })}
                />
                Time on it is billable
              </label>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
